import { Service } from 'constants/usercentrics';
import { useContext, useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import CarouselContainer from 'components/Shared/Carousel/CarouselContainer';
import RichText from 'components/ui/ContentBlocks/RichText';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import Highlight from 'components/ui/Highlight';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import { CookieContext } from 'context/cookie';
import { getCountryAndCity } from 'data/utils/clinic';
import { useTranslation } from 'hooks/useTranslation';
import { RecordType } from 'types/BeforeAfterPhotos';
import { default as Carousel } from './Carousel';
import type { ClinicEntry } from '.';

type Props = {
  clinic: ClinicEntry;
};

const Clinic = ({ clinic }: Props): JSX.Element => {
  const {
    address,
    city: cityObj,
    descriptionRich,
    highlight1,
    highlight2,
    highlight3,
    location,
    photos,
  } = clinic;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { city, country } = getCountryAndCity(cityObj);
  const { t } = useTranslation();
  const { cookies } = useContext(CookieContext);
  const [allowed, setAllowed] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const photoItems = photos?.items || [];
  const iconColor = '#ffca58';

  const handleGalleryOpen = (): void => {
    setIsGalleryOpen(true);
  };

  const handleGalleryClose = (): void => {
    setIsGalleryOpen(false);
  };

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, Service.GOOGLE_MAPS);

    setAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  return (
    <div className="max-w-screen-xl mx-auto px-5 lg:px-4 lg:grid md:grid-cols-3 md:mt-16 lg:mt-24">
      <div>
        <h2 className="adelle-bold-section-headline">
          {t('doctorProfile.clinic.title')}
        </h2>
        <div className="border overflow-hidden rounded w-full lg:w-64 mb-4">
          {!allowed ? (
            <EmbedPlaceholder service={Service.GOOGLE_MAPS} />
          ) : (
            <LazyLoad classNamePrefix="location-map" offset={200} once>
              <iframe
                width="100%"
                height="280"
                id="gmap_canvas"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  `${location?.lat},${location?.lon}`,
                )}&t=&z=17&ie=UTF8&iwloc=&output=embed`}
              />
            </LazyLoad>
          )}
          <div className="p-3 flex">
            <Icon
              className="mr-3 w-4 shrink-0"
              type="LocationPin"
              color={iconColor}
            />
            <p>
              {address}
              <br />
              <span className="font-bold">
                {city}, {country.name}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="col-span-2 lg:pl-12">
        {descriptionRich && (
          <div className="text-primary">
            <RichText fields={descriptionRich} />
          </div>
        )}
        {(highlight1 || highlight2 || highlight3) && (
          <ul className="my-6">
            {highlight1 && (
              <Highlight
                highlight={highlight1}
                wrapper="li"
                wrapperClasses="text-primary"
                icon="RoundCheck"
                iconColor={iconColor}
              />
            )}
            {highlight2 && (
              <Highlight
                highlight={highlight2}
                wrapper="li"
                wrapperClasses="text-primary"
                icon="RoundCheck"
                iconColor={iconColor}
              />
            )}
            {highlight3 && (
              <Highlight
                highlight={highlight3}
                wrapper="li"
                wrapperClasses="text-primary"
                icon="RoundCheck"
                iconColor={iconColor}
              />
            )}
          </ul>
        )}
        {!!photoItems.length && (
          <div className="hidden lg:grid grid-cols-2 gap-4">
            {photoItems.map((photo, i) => {
              if (i > 1) {
                return null;
              }

              return (
                <div
                  key={i}
                  className="relative"
                  style={{ height: '280px', width: '384px' }}
                >
                  <Image
                    className="object-cover object-center"
                    src={photo.url}
                    alt={photo.description || photo.title || ''}
                    fill
                  />
                </div>
              );
            })}
          </div>
        )}
        <div className="hidden lg:block text-right py-4">
          {photoItems.length > 2 && (
            <span
              className="text-primary underline cursor-pointer"
              onClick={handleGalleryOpen}
            >
              {t('doctorProfile.viewPhotos')} ({photoItems.length})
            </span>
          )}
        </div>
        <Carousel className="lg:hidden" slides={photoItems} />
      </div>
      {isGalleryOpen && (
        <CarouselContainer
          className="hidden lg:block"
          slides={photoItems}
          pageType={RecordType.CLINIC}
          onClose={handleGalleryClose}
          slug={clinic.slug as string}
        />
      )}
    </div>
  );
};

export default Clinic;
